/**
 * ProofPhotoRow — một hàng ảnh bằng chứng có tiêu đề ("TRƯỚC TRANG ĐIỂM · 2
 * ảnh"), đủ mọi tấm kèm giờ chụp, bấm xem lớn, và nút ✕ trên tấm người xem
 * được gỡ.
 *
 * CEO chốt 30/09/2026: cho gửi nhiều tấm nhưng màn chỉ hiện một, và chi tiết
 * đơn không phân biệt được trước/sau. Thẻ ca (Lịch ê-kíp, lịch chi tiết) và mục
 * "Bằng chứng" của chi tiết đơn dùng CHUNG hàng này nên hai nơi luôn khớp.
 *
 * Nút ✕ chỉ hiện khi máy chủ bật `proof.removable` — màn không tự suy luật
 * người chụp / hạn nộp. Gỡ luôn hỏi lại trước: ảnh là bằng chứng của đơn.
 */
import { toast } from "sonner";
import { PhotoThumbList, PhotoThumbRemove } from "@/components/PhotoThumb";
import { useDeleteProof } from "./api";
import type { ProofPhoto } from "./utils";

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
}

interface Props {
  orderId: string;
  /** "Trước trang điểm" / "Sau trang điểm" — tiêu đề hàng và nhãn cho người đọc màn hình. */
  label: string;
  photos: ProofPhoto[];
  /** Ghi tên người chụp dưới ảnh — bật ở chi tiết đơn, tắt ở thẻ ca (toàn ảnh của mình). */
  showTaker?: boolean;
  /** Câu hiện khi hàng chưa có ảnh; vắng thì hàng rỗng không vẽ gì. */
  emptyText?: string;
}

export function ProofPhotoRow({ orderId, label, photos, showTaker = false, emptyText }: Props) {
  const delM = useDeleteProof();

  const remove = (ph: ProofPhoto, i: number) => {
    const last = photos.length === 1;
    const ask = last
      ? `Xoá ảnh ${label.toLowerCase()} này? Đây là tấm cuối — xoá xong phải chụp lại mới hoàn tất được ca.`
      : `Xoá ảnh ${label.toLowerCase()} số ${i + 1}?`;
    if (!window.confirm(ask)) return;
    delM.mutate(
      { proofId: ph.proof.id, orderId, image: ph.src },
      {
        onSuccess: () => toast.success("Đã xoá ảnh"),
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : "Xoá ảnh thất bại — thử lại"),
      },
    );
  };

  return (
    <div className="min-w-0">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {label} · {photos.length} ảnh
      </p>
      {photos.length === 0 ? (
        emptyText ? <p className="text-xs text-muted-foreground">{emptyText}</p> : null
      ) : (
        <PhotoThumbList
          images={photos.map((p) => p.src)}
          label={label}
          alt={(i) => `${label} ${i + 1}`}
          openLabel={(i) => `Xem lớn ảnh ${label.toLowerCase()} ${i + 1}`}
          overlay={(_, i) => {
            const ph = photos[i]!;
            return ph.proof.removable ? (
              <PhotoThumbRemove
                label={`Xoá ảnh ${label.toLowerCase()} ${i + 1}`}
                disabled={delM.isPending}
                onClick={() => remove(ph, i)}
              />
            ) : null;
          }}
          caption={(_, i) => {
            const p = photos[i]!.proof;
            return (
              <div className="mt-0.5 max-w-[240px] truncate text-xs text-muted-foreground">
                {showTaker ? `${p.takenByName} · ` : ""}
                {fmtTime(p.createdAt)}
              </div>
            );
          }}
        />
      )}
    </div>
  );
}
